import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import { CollapseContent, ExpandContent, SettingsApplications } from '@tempo-io/sui-icons';
import { Separator } from '@tempo-io/sui-separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@tempo-io/sui-sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@tempo-io/sui-tabs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPublishedItemDetailsAdapter } from '@/components/modular-item-panel/adapters/item-details/createPublishedItemDetailsAdapter';
import { createPublishedSystemFieldsAdapter } from '@/components/modular-item-panel/adapters/system-fields/createPublishedSystemFieldsAdapter';
import { TabAdapterProvider } from '@/components/modular-item-panel/context/TabAdapterContext';
import { TooltipIconButton } from '@/components/ui/tooltipIconButton';
import { PublishedItemDetails } from './PublishedItemDetails';
import { PublishedSystemFields } from './PublishedSystemFields';
import type { IdentifiableTItem, ItemDetails } from './types';

type TabId = 'system';

interface PublishedItemPanelProps {
  itemDetails: ItemDetails | null;
  enrichedItem: IdentifiableTItem | null;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onClose: () => void;
}

export const PublishedItemPanel = ({
  itemDetails,
  enrichedItem,
  expanded,
  onExpandedChange,
  onClose,
}: PublishedItemPanelProps) => {
  const { t } = useTranslation();
  const [panelTab, setPanelTab] = useState<TabId>('system');

  const itemDetailsAdapter =
    itemDetails && enrichedItem
      ? createPublishedItemDetailsAdapter({ itemDetails, enrichedItem })
      : null;

  const systemFieldsAdapter = itemDetails
    ? createPublishedSystemFieldsAdapter({ itemDetails })
    : null;

  const toggleExpanded = () => {
    onExpandedChange(!expanded);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const isOpen = itemDetails !== null && enrichedItem !== null;

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        className={cn('flex flex-col gap-0', { '!max-w-7xl': expanded, '!max-w-lg': !expanded })}
      >
        <SheetHeader className="border-b-.5 border-gray-200 px-4 pb-2">
          <SheetTitle className="sr-only" />
          <TooltipIconButton
            className="padding-3 absolute top-3.5 right-9 cursor-pointer rounded-xs hover:bg-gray-300"
            icon={expanded ? <CollapseContent /> : <ExpandContent />}
            tooltipText={
              expanded
                ? t('adaptivePlanner.itemPanel.collapsePanel')
                : t('adaptivePlanner.itemPanel.expandPanel')
            }
            onClick={toggleExpanded}
          />
        </SheetHeader>
        <SheetDescription className="sr-only" />
        <Separator />
        <div className="flex h-full flex-row overflow-hidden">
          {expanded && itemDetailsAdapter && (
            <div className="flex-grow border-r-1 border-gray-200 p-4 overflow-y-auto">
              <TabAdapterProvider adapter={itemDetailsAdapter}>
                <PublishedItemDetails />
              </TabAdapterProvider>
            </div>
          )}
          <Tabs
            value={panelTab}
            className="flex h-full w-full max-w-lg flex-row gap-0 overflow-hidden"
          >
            <div className="flex-1 border-r-1 border-gray-200 bg-sidebar-primary-foreground p-4 overflow-y-auto">
              <TabsContent value="system">
                {systemFieldsAdapter && (
                  <TabAdapterProvider adapter={systemFieldsAdapter}>
                    <PublishedSystemFields />
                  </TabAdapterProvider>
                )}
              </TabsContent>
            </div>

            <TabsList className="flex min-h-[7rem] h-max flex-col bg-background">
              <TabsTrigger
                className={cn('cursor-pointer', panelTab === 'system' && 'bg-zinc-100')}
                value="system"
                onClick={() => setPanelTab('system')}
                asChild
              >
                <TooltipIconButton
                  icon={<SettingsApplications />}
                  tooltipText={t('adaptivePlanner.itemPanel.systemFields')}
                />
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <SheetFooter className="border-t-1 border-gray-200 items-end">
          <SheetClose asChild>
            <Button type="submit">{t('common.close')}</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
